import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as DoneIcon,
  MoreVert as MoreIcon,
  SubdirectoryArrowRight as SubtaskIcon,
  Event as DueIcon,
} from '@mui/icons-material';
import { PRIORITIES } from './BoardTaskDialog';

const LIST_WIDTH = 290;

export const isOverdue = (task) =>
  !task.is_done && task.due_date && new Date(task.due_date) < new Date(new Date().toDateString());

const initials = (name = '') => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');

const TaskCard = ({ task, parentTitle, subtaskStats, onOpen, provided, snapshot }) => {
  const priority = PRIORITIES.find((p) => p.value === task.priority);
  return (
    <Paper
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      onClick={() => onOpen(task)}
      elevation={snapshot.isDragging ? 6 : 1}
      sx={{
        p: 1.25,
        mb: 1,
        cursor: 'pointer',
        borderLeft: 3,
        borderLeftColor: task.priority === 'high' ? 'error.main' : task.priority === 'medium' ? 'warning.main' : 'divider',
        opacity: task.is_done ? 0.7 : 1,
        '&:hover': { boxShadow: 3 },
      }}
    >
      {parentTitle && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, color: 'text.secondary' }}>
          <SubtaskIcon sx={{ fontSize: 14 }} />
          <Typography variant="caption" noWrap>{parentTitle}</Typography>
        </Box>
      )}
      <Typography variant="body2" fontWeight={500} sx={{ textDecoration: task.is_done ? 'line-through' : 'none', wordBreak: 'break-word' }}>
        {task.title}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
        {priority && task.priority !== 'medium' && (
          <Chip size="small" label={priority.label} color={priority.color} variant="outlined" sx={{ height: 20 }} />
        )}
        {task.due_date && (
          <Chip
            size="small"
            icon={<DueIcon sx={{ fontSize: 14 }} />}
            label={new Date(task.due_date).toLocaleDateString()}
            color={isOverdue(task) ? 'error' : 'default'}
            variant={isOverdue(task) ? 'filled' : 'outlined'}
            sx={{ height: 20 }}
          />
        )}
        {subtaskStats.total > 0 && (
          <Tooltip title="Subtasks done">
            <Chip size="small" icon={<DoneIcon sx={{ fontSize: 14 }} />} label={`${subtaskStats.done}/${subtaskStats.total}`} sx={{ height: 20 }} />
          </Tooltip>
        )}
        {task.linked_section_title && (
          <Chip size="small" label={task.linked_section_title} variant="outlined" sx={{ height: 20, maxWidth: 120 }} />
        )}
        <Box sx={{ flex: 1 }} />
        {task.assignee_names?.length > 0 && (
          <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 22, height: 22, fontSize: 10 } }}>
            {task.assignee_names.map((a) => (
              <Tooltip key={a.id} title={a.name}><Avatar>{initials(a.name)}</Avatar></Tooltip>
            ))}
          </AvatarGroup>
        )}
      </Box>
    </Paper>
  );
};

const QuickAdd = ({ onAdd }) => {
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');

  const submit = async () => {
    const value = title.trim();
    if (!value) { setAdding(false); return; }
    await onAdd(value);
    setTitle('');
  };

  if (!adding) {
    return (
      <Button size="small" startIcon={<AddIcon />} onClick={() => setAdding(true)} sx={{ justifyContent: 'flex-start', color: 'text.secondary' }} fullWidth>
        Add task
      </Button>
    );
  }
  return (
    <TextField
      autoFocus
      size="small"
      fullWidth
      placeholder="Task title, then Enter"
      value={title}
      onChange={(e) => setTitle(e.target.value)}
      onBlur={submit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') submit();
        if (e.key === 'Escape') { setTitle(''); setAdding(false); }
      }}
    />
  );
};

const KanbanBoard = ({
  lists, tasks, visibleTaskIds, canManage,
  onOpenTask, onQuickAdd, onMoveTask, onReorderLists,
  onAddList, onRenameList, onToggleDoneList, onDeleteList,
}) => {
  const [menu, setMenu] = useState(null); // { anchor, list }
  const [newListName, setNewListName] = useState('');

  const taskById = Object.fromEntries(tasks.map((t) => [t.id, t]));
  const subtaskStats = (taskId) => {
    const children = tasks.filter((t) => t.parent === taskId);
    return { total: children.length, done: children.filter((t) => t.is_done).length };
  };
  const tasksIn = (listId) =>
    tasks.filter((t) => t.board_list === listId).sort((a, b) => a.position - b.position);

  const handleDragEnd = ({ source, destination, draggableId, type }) => {
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;
    if (type === 'LIST') {
      const ids = lists.map((l) => l.id);
      ids.splice(source.index, 1);
      ids.splice(destination.index, 0, draggableId);
      onReorderLists(ids);
      return;
    }
    // Indexes are relative to the visible (filtered) cards; map back onto the full list.
    const destVisible = tasksIn(destination.droppableId).filter((t) => visibleTaskIds.has(t.id) && t.id !== draggableId);
    const anchor = destVisible[destination.index];
    const destAll = tasksIn(destination.droppableId).filter((t) => t.id !== draggableId).map((t) => t.id);
    const insertAt = anchor ? destAll.indexOf(anchor.id) : destAll.length;
    destAll.splice(insertAt, 0, draggableId);
    onMoveTask(draggableId, destination.droppableId, destAll);
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="board" direction="horizontal" type="LIST" isDropDisabled={!canManage}>
        {(boardProvided) => (
          <Box
            ref={boardProvided.innerRef}
            {...boardProvided.droppableProps}
            sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2, alignItems: 'flex-start', minHeight: 300 }}
          >
            {lists.map((list, index) => {
              const listTasks = tasksIn(list.id).filter((t) => visibleTaskIds.has(t.id));
              return (
                <Draggable key={list.id} draggableId={list.id} index={index} isDragDisabled={!canManage}>
                  {(listProvided) => (
                    <Paper
                      ref={listProvided.innerRef}
                      {...listProvided.draggableProps}
                      variant="outlined"
                      sx={{ width: LIST_WIDTH, flexShrink: 0, bgcolor: 'action.hover', display: 'flex', flexDirection: 'column', maxHeight: '70vh' }}
                    >
                      <Box {...listProvided.dragHandleProps}
                        sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 1, cursor: canManage ? 'grab' : 'default' }}>
                        {list.is_done_list && <DoneIcon fontSize="small" color="success" sx={{ mr: 0.75 }} />}
                        <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }} noWrap>{list.name}</Typography>
                        <Chip size="small" label={listTasks.length} sx={{ height: 20, mr: 0.5 }} />
                        {canManage && (
                          <IconButton size="small" onClick={(e) => setMenu({ anchor: e.currentTarget, list })} aria-label="List options">
                            <MoreIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                      <Droppable droppableId={list.id} type="TASK">
                        {(provided, snapshot) => (
                          <Box
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            sx={{
                              px: 1, minHeight: 40, overflowY: 'auto', flex: 1,
                              bgcolor: snapshot.isDraggingOver ? 'action.selected' : 'transparent',
                              transition: 'background-color 0.2s',
                            }}
                          >
                            {listTasks.map((task, i) => (
                              <Draggable key={task.id} draggableId={task.id} index={i}>
                                {(cardProvided, cardSnapshot) => (
                                  <TaskCard
                                    task={task}
                                    parentTitle={task.parent ? taskById[task.parent]?.title : null}
                                    subtaskStats={subtaskStats(task.id)}
                                    onOpen={onOpenTask}
                                    provided={cardProvided}
                                    snapshot={cardSnapshot}
                                  />
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </Box>
                        )}
                      </Droppable>
                      <Box sx={{ p: 1 }}>
                        <QuickAdd onAdd={(title) => onQuickAdd(list.id, title)} />
                      </Box>
                    </Paper>
                  )}
                </Draggable>
              );
            })}
            {boardProvided.placeholder}

            {canManage && (
              <Paper variant="outlined" sx={{ width: LIST_WIDTH, flexShrink: 0, p: 1.5, borderStyle: 'dashed' }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="New list name"
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newListName.trim()) { onAddList(newListName.trim()); setNewListName(''); }
                  }}
                />
                <Button
                  size="small" startIcon={<AddIcon />} sx={{ mt: 1 }} disabled={!newListName.trim()}
                  onClick={() => { onAddList(newListName.trim()); setNewListName(''); }}
                >
                  Add list
                </Button>
              </Paper>
            )}
          </Box>
        )}
      </Droppable>

      <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={() => setMenu(null)} sx={{ zIndex: 10001 }}>
        <MenuItem onClick={() => { onRenameList(menu.list); setMenu(null); }}>Rename</MenuItem>
        <MenuItem onClick={() => { onToggleDoneList(menu.list); setMenu(null); }}>
          {menu?.list.is_done_list ? 'Unmark as "done" list' : 'Mark as "done" list'}
        </MenuItem>
        <MenuItem onClick={() => { onDeleteList(menu.list); setMenu(null); }} sx={{ color: 'error.main' }}>Delete list</MenuItem>
      </Menu>
    </DragDropContext>
  );
};

export default KanbanBoard;
